import { marked } from 'marked'
import mermaid from 'mermaid'
import katex from 'katex'
import hljs from 'highlight.js'
import 'highlight.js/styles/default.css'

mermaid.initialize({ startOnLoad: false })

// 扩展marked的渲染规则，处理表格表头滑动
const originalRenderer = new marked.Renderer()

// 自定义表格渲染
originalRenderer.table = function (header, body) {
  return `
    <div class="table-container">
      <table>
        <thead>${header}</thead>
        <tbody>${body}</tbody>
      </table>
    </div>
  `
}

// 自定义代码块渲染，添加对mermaid的特殊处理
originalRenderer.code = function (code, lang) {
  if (lang === 'mermaid') {
    // 这里可以直接渲染mermaid代码，或者进行其他处理
    return `<pre class=""><code>${code}</code></pre>`
  } else {
    const options = {
      language: lang || 'plaintext',
      ignoreIllegals: true,
    }
    const highlightedCode = hljs.highlight(code, options).value
    const langClass = lang ? ` language-${lang}` : ''
    return `<pre class="code-block${langClass}"><code>${highlightedCode}</code></pre>`
  }
}

marked.setOptions({
  renderer: originalRenderer,
  gfm: true,
  breaks: false,
  pedantic: false,
  sanitize: false,
  smartLists: true,
  smartypants: false,
  headerIds: true,
})

// 渲染 Mermaid 代码
async function renderMermaid(mermaidData) {
  // 步骤 1: 去除首尾空格
  mermaidData = mermaidData.trim()

  // 步骤 2 确保 "pie" 后有换行符
  if (mermaidData.includes('pie') && !mermaidData.includes('pie\n')) {
    mermaidData = mermaidData.replace('pie', 'pie\n')
  }

  // 步骤 3: 处理 "pie" 和 "title" 之间多余的文字
  if (mermaidData.includes('pie') && mermaidData.includes('title')) {
    const startIndex = mermaidData.indexOf('pie') + 'pie'.length
    const endIndex = mermaidData.indexOf('title')
    if (startIndex !== -1 && endIndex !== -1) {
      mermaidData = mermaidData.slice(0, startIndex) + '\n    ' + mermaidData.slice(endIndex)
    }
  }

  // 步骤 4: 处理每行数据，去除多余空格
  mermaidData = mermaidData
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line) // 过滤掉空行
    .join('\n    ')

  try {
    const result = await mermaid.render('mermaid-' + Date.now(), mermaidData)
    const svg = result.svg
    return `<div class="mermaid">${svg}</div>\n\n`
  } catch (error) {
    console.log('Mermaid 渲染出错:', error)
    return `<pre class="code-block language-mermaid"><code>${mermaidData}</code></pre>`
  }
}

// 渲染 Markdown 代码块
async function renderMarkdownCodeBlock(markdownData) {
  const innerMarkdown = markdownData.trim()
  try {
    const renderedInnerMarkdown = await marked(innerMarkdown)
    return renderedInnerMarkdown
  } catch (error) {
    console.error('Markdown代码块渲染出错:', error)
    return `<pre class="code-block language-markdown"><code>${markdownData}</code></pre>`
  }
}

// 渲染公式
function renderFormula(formula) {
  try {
    const renderedFormula = katex.renderToString(formula, { throwOnError: false })
    return `<span class="katex">${renderedFormula}</span>`
  } catch (error) {
    return `<pre>${formula}</pre>`
  }
}

export default {
  async dataRender(inputMarkdown) {
    // 使用正则表达式匹配 **数字** 的模式，并在结尾的 ** 后添加一个空格
    inputMarkdown = inputMarkdown.replace(/\*\*([^**]+)\*\*/g, ' **$1** ')
    const lines = inputMarkdown.split('\n')
    let tempContent = ''
    let mermaidData = ''
    let formulaData = ''
    let markdownData = ''
    let isCollectingMermaid = false
    let isCollectingFormula = false
    let isCollectingMarkdown = false

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i]
      // 检查行首的 # 数量
      const hashCount = (line.match(/^#+/g) || [''])[0].length
      if (hashCount > 6) {
        // 若 # 数量超过 6 个，清除所有 # 及可能的空格
        line = line.replace(/^#+\s*/, '')
      }

      if (line.startsWith('```mermaid')) {
        isCollectingMermaid = true
        continue
      }

      if (isCollectingMermaid) {
        if (line.startsWith('```')) {
          isCollectingMermaid = false
          tempContent += await renderMermaid(mermaidData)
          mermaidData = ''
          continue
        }
        mermaidData += line + '\n'
        continue
      }

      if (line.startsWith('```markdown')) {
        isCollectingMarkdown = true
        continue
      }

      if (isCollectingMarkdown) {
        if (line.startsWith('```')) {
          isCollectingMarkdown = false
          tempContent += markdownData
          markdownData = ''
          continue
        }
        markdownData += line + '\n'
        continue
      }

      if (line.startsWith('$$')) {
        isCollectingFormula = true
        if (line.endsWith('$$')) {
          const formula = line.slice(2, -2)
          tempContent += renderFormula(formula)
          isCollectingFormula = false
          continue
        }
        formulaData = line.slice(2) + '\n'
        continue
      }

      if (isCollectingFormula) {
        if (line.endsWith('$$')) {
          isCollectingFormula = false
          const formula = formulaData + line.slice(0, -2)
          tempContent += renderFormula(formula)
          formulaData = ''
          continue
        }
        formulaData += line + '\n'
        continue
      }

      tempContent += line + '\n'
    }

    return marked(tempContent)
  },
}
